import React, { useState, useMemo } from "react";
import Select, { components } from "react-select";
import * as XLSX from "xlsx";
import * as FeatherIcon from "react-feather";
import { searchCompetitor } from "../../../services/accIqService";
import Heatmap from "../../TableComponents/Heatmap";
import APP_CONFIG from "../../../config/appConfig";

// ── Keep your full maps here ──────────────────────────────────────────────────

// ++++++++++++++++++++++++++++++++++++++++++++++

const buildGroupedMetricOptions = (availableMetrics) => {
  return Object.entries(APP_CONFIG?.METRIC_GROUPS)
    .map(([group, metrics]) => {
      const options = metrics
        .filter((metric) => availableMetrics.includes(metric))
        .map((metric) => ({
          label: formatMetricLabel(metric),
          value: metric,
        }));

      return options.length
        ? {
            label: group,
            options,
          }
        : null;
    })
    .filter(Boolean);
};

const CollapsibleGroupHeading = (props) => {
  const { Heading, getStyles, theme, selectProps, cx } = props;

  const { collapsedGroups, setCollapsedGroups } = selectProps;

  const label = props.data.label;

  const isCollapsed = collapsedGroups?.includes(label);

  const toggleGroup = (e) => {
    e.stopPropagation();

    setCollapsedGroups((prev) =>
      isCollapsed ? prev.filter((g) => g !== label) : [...prev, label]
    );
  };

  return (
    <div
      onClick={toggleGroup}
      style={{
        cursor: "pointer",
        fontWeight: 600,
        padding: "8px 12px",
        background: "#f8f9fa",
        borderBottom: "1px solid #eee",
      }}
    >
      {isCollapsed ? (
        <FeatherIcon.ChevronRight width={16} className="" />
      ) : (
        <FeatherIcon.ChevronDown width={16} className="" />
      )}{" "}
      {label}
    </div>
  );
};

const CollapsibleGroup = (props) => {
  const { children, Heading, data, selectProps } = props;

  const { collapsedGroups } = selectProps;

  const isCollapsed = collapsedGroups?.includes(data.label);

  return (
    <div>
      <Heading {...props} />
      {!isCollapsed && children}
    </div>
  );
};
// ++++++++++++++++++++++++++++++++++++++++++++++

const DEFAULT_METRICS = [
  "IQ_TOTAL_REV",
  "IQ_GP",
  "IQ_EBITDA",
  "IQ_EBIT",
  "IQ_NI",
  "IQ_BASIC_EPS_INCL",
  "IQ_TOTAL_ASSETS",
  "IQ_TOTAL_CAP",
];

const COMPANIES_SHOW_DEFAULT = 5;

const formatMetricLabel = (metric) =>
  APP_CONFIG?.METRICS_WITH_NAMES[metric] || metric.replace(/IQ_|_/g, " ");

const mergeUniqueCompanies = (...arrays) => {
  const map = new Map();
  arrays.flat().forEach((item) => {
    if (item?.value) map.set(item.value, item);
  });
  return Array.from(map.values());
};

// Matches GroupedBarChart's CheckboxOption exactly
const CheckboxOption = (props) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8 }}
    />
    {props.label}
  </components.Option>
);

/**
 * CompetitorHeatmapContainer
 *
 * Props from parent (FinancialPerformance):
 *  - selectedCompetitors    : master selected list (drives the top-level selector)
 *  - setSelectedCompetitors : update master selected list
 *  - competitorOptions      : all available options for the search dropdown
 *  - setCompetitorOptions   : update available options (for search results merge)
 *  - financialData          : fetched financials from parent
 *  - loadingFinancials      : loading flag from parent
 *  - fetchFinancials        : fetches financial data (no save)
 *  - saveHeatmapRecord      : persists heatmapCompanies to the server
 *  - defaultCompetitors     : default competitor list for search merge
 *
 * Heatmap-local state:
 *  - heatmapCompanies : companies shown in the heatmap visual (subset of selectedCompetitors)
 *  - selectedMetrics  : metrics shown in the heatmap visual
 */
const CompetitorHeatmapContainer = ({
  selectedCompetitors,
  setSelectedCompetitors,
  competitorOptions,
  setCompetitorOptions,
  financialData,
  loadingFinancials,
  fetchFinancials,
  saveHeatmapRecord,
  defaultCompetitors = [],
}) => {
  // ── local UI state ────────────────────────────────────────────────────────
  const [compLoading, setCompLoading] = useState(false);

  // heatmapCompanies: local subset of selectedCompetitors shown in heatmap visual.
  // Starts equal to selectedCompetitors; user can remove companies here without
  // affecting the master selector or the bar chart.
  const [heatmapCompanies, setHeatmapCompanies] = useState(selectedCompetitors);

  // Sync heatmapCompanies when parent selectedCompetitors changes
  const prevSelectedRef = React.useRef(selectedCompetitors);
  React.useEffect(() => {
    const prev = prevSelectedRef.current;
    // Add any newly added companies automatically into heatmap
    const added = selectedCompetitors.filter(
      (c) => !prev.find((p) => p.value === c.value)
    );
    if (added.length) {
      setHeatmapCompanies((curr) => mergeUniqueCompanies(curr, added));
    }
    // Remove companies that were removed from the master selector
    setHeatmapCompanies((curr) =>
      curr.filter((c) => selectedCompetitors.find((s) => s.value === c.value))
    );
    prevSelectedRef.current = selectedCompetitors;
  }, [selectedCompetitors]);

  // Show more/less for heatmap company chips
  const [showAllHeatmapCompanies, setShowAllHeatmapCompanies] = useState(false);

  // Heatmap company filter panel open/close (matches GroupedBarChart pattern)
  const [showCompanyFilter, setShowCompanyFilter] = useState(false);

  // Heatmap-local metric filter
  const [selectedMetrics, setSelectedMetrics] = useState(DEFAULT_METRICS);
  const [metricDraft, setMetricDraft] = useState(DEFAULT_METRICS);
  const [showMetricFilter, setShowMetricFilter] = useState(false);
  const [updatingMetrics, setUpdatingMetrics] = useState(false);

  const [collapsedGroups, setCollapsedGroups] = useState(
    Object.keys(APP_CONFIG?.METRIC_GROUPS)
  );
  // ── search companies (top-level selector) ────────────────────────────────
  const searchCompanies = async (searchString) => {
    if (!searchString || searchString.length < 3) {
      setCompetitorOptions(
        mergeUniqueCompanies(selectedCompetitors, defaultCompetitors)
      );
      return;
    }
    try {
      setCompLoading(true);
      const response = await searchCompetitor(searchString);
      const list = Array.isArray(response)
        ? response
        : response?.data || response?.result || [];
      const options = list.map((item) => ({
        label: item.companyname,
        value: item.companyid,
      }));
      setCompetitorOptions(
        mergeUniqueCompanies(selectedCompetitors, defaultCompetitors, options)
      );
    } catch (error) {
      console.error("Search error:", error);
    }
    setCompLoading(false);
  };

  const handleInputChange = (inputValue, meta) => {
    if (meta.action === "input-change") searchCompanies(inputValue);
    return inputValue;
  };

  // ── generate heatmap: save heatmapCompanies then fetch ───────────────────
  // const handleGenerateHeatmap = async () => {
  //   // Save only the heatmap-visible companies (not the full master list)
  //   await saveHeatmapRecord(heatmapCompanies);
  //   // Fetch financials for the full master selected list
  //   fetchFinancials(selectedCompetitors);
  // };

  const handleGenerateHeatmap = async () => {
    await saveHeatmapRecord(heatmapCompanies);
    fetchFinancials(heatmapCompanies);
  };

  // ── heatmap metric filter ─────────────────────────────────────────────────
  const applyMetricFilter = () => {
    if (metricDraft.length === 0) return;
    setUpdatingMetrics(true);
    setTimeout(() => {
      setSelectedMetrics([...metricDraft]);
      setShowMetricFilter(false);
      setUpdatingMetrics(false);
    }, 500);
  };

  // ── export (only heatmap-visible companies) ───────────────────────────────
  const exportHeatmap = () => {
    if (!financialData.length) return;

    const rows = financialData
      .map((company) => {
        if (
          !heatmapCompanies.find((c) => c.value === company.vendor_company_id)
        )
          return null;

        const row = { "Company Name": company.company_name };
        let hasValue = false;

        selectedMetrics.forEach((metric) => {
          const found = company.metrics.find((m) => m.metric === metric);
          const value = found?.value ?? "";
          row[formatMetricLabel(metric)] = value;
          if (value !== "") hasValue = true;
        });

        return hasValue ? row : null;
      })
      .filter(Boolean);

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Heatmap");
    XLSX.writeFile(wb, "Competitor_Heatmap.xlsx");
  };

  // const metricOptions = [...new Set(ALL_METRICS)].map((m) => ({
  //   label: formatMetricLabel(m),
  //   value: m,
  // }));
  const metricOptions = buildGroupedMetricOptions([
    ...new Set(APP_CONFIG?.ALL_METRICS),
  ]);

  // Chips: show limited or all
  const visibleHeatmapChips = showAllHeatmapCompanies
    ? heatmapCompanies
    : heatmapCompanies.slice(0, COMPANIES_SHOW_DEFAULT);

  const sortedMetricOptions = useMemo(() => {
    return metricOptions.map((group) => {
      const selected = [];
      const unselected = [];

      group.options.forEach((option) => {
        if (metricDraft.includes(option.value)) {
          selected.push(option);
        } else {
          unselected.push(option);
        }
      });

      return {
        ...group,
        options: [...selected, ...unselected],
      };
    });
  }, [metricOptions, metricDraft]);

  return (
    <div className="container mt-4 heatmap_wrapper">
      <h3 className="mb-3 heatmap_head">Competitor Financial Heatmap</h3>

      {/* ── Top controls: search + generate + filter buttons ── */}
      <div className="d-flex align-items-center flex-wrap heatmap_dropdown_block">
        {/* Master company search selector */}
        <div className="heatmap_dropdown">
          <label className="form-label heatmap_label">Select Competitors</label>
          <Select
            isMulti
            options={competitorOptions}
            value={selectedCompetitors}
            isLoading={compLoading}
            className="font13 search_competitors_select"
            styles={APP_CONFIG?.CUSTOM_SELECT_STYLES}
            placeholder="Search competitors..."
            onInputChange={handleInputChange}
            onChange={(list) => setSelectedCompetitors(list || [])}
            closeMenuOnSelect={false}
            isClearable={false}
            backspaceRemovesValue={false}
            hideSelectedOptions={false}
            components={{ Option: CheckboxOption }}
            controlShouldRenderValue={false}
          />
        </div>

        <button
          className="btn btn-primary mt-4 ml-3 heatmap_generate_btn"
          onClick={handleGenerateHeatmap}
          disabled={loadingFinancials || selectedCompetitors.length === 0}
        >
          {loadingFinancials ? "Generating Heatmap..." : "Generate Heatmap"}
        </button>

        <div className="col-md-12">
          {/* Filter Companies button — matches GroupedBarChart style */}
          <button
            // className="btn mt-4 ml-3 heatmap_filter_metrics"
            className="btn mt-2 heatmap_filter_metrics"
            onClick={() => {
              setShowCompanyFilter((prev) => !prev);
              setShowMetricFilter(false);
            }}
          >
            Filter Companies
          </button>

          <button
            className="btn mt-2 ml-3 heatmap_filter_metrics"
            onClick={() => {
              setMetricDraft(selectedMetrics);
              setShowMetricFilter(!showMetricFilter);
              setShowCompanyFilter(false);
            }}
          >
            Filter Metrics
          </button>

          {financialData.length > 0 && (
            <button
              className="btn mt-2 ml-3 heatmap_filter_export"
              onClick={exportHeatmap}
            >
              Export Heatmap
            </button>
          )}
        </div>
      </div>

      {loadingFinancials && (
        <div className="mb-2 text-primary font13 genarating_heatmap_text">
          Generating heatmap, please wait...
        </div>
      )}

      {/* ── Heatmap company filter panel — matches GroupedBarChart layout exactly ── */}
      {showCompanyFilter && (
        <div className="card p-3 mt-3 select_heatmap_card">
          <label className="font14 fw-medium mb-2 select_heatmap_card_label">
            Select Companies
          </label>

          {/*
            Select with controlShouldRenderValue={false}:
            selected chips are hidden inside the input and shown as badges below.
            Options pool = full master selectedCompetitors list.
          */}
          <Select
            isMulti
            options={mergeUniqueCompanies(selectedCompetitors)}
            value={heatmapCompanies}
            className="font13 search_competitors_select"
            placeholder="Search companies..."
            styles={APP_CONFIG?.CUSTOM_SELECT_STYLES}
            closeMenuOnSelect={false}
            isClearable={false}
            backspaceRemovesValue={false}
            hideSelectedOptions={false}
            components={{ Option: CheckboxOption }}
            controlShouldRenderValue={false}
            onChange={(list) => setHeatmapCompanies(list || [])}
          />

          <div className="mt-2 select_heatmap_apply_block">
            <button
              className="btn btn-light btn-sm ml-2 select_heatmap_cancel_btn"
              onClick={() => setShowCompanyFilter(false)}
            >
              Close
            </button>
          </div>

          <small className="text-muted mt-2">
            Selected: {heatmapCompanies.length}
          </small>
        </div>
      )}

      {/* ── Heatmap metric filter panel ── */}
      {showMetricFilter && (
        <div className="card p-3 mt-3 select_heatmap_card">
          <label className="font14 fw-medium mb-2 select_heatmap_card_label">
            Select Metrics (Min 5, Max 8)
          </label>

          {/* <Select
            isMulti
            closeMenuOnSelect={false}
            options={metricOptions}
            value={metricOptions.filter((x) => metricDraft.includes(x.value))}
            onChange={(list) =>
              setMetricDraft([
                ...new Set((list || []).map((item) => item.value)),
              ])
            }
            components={{
              Group: CollapsibleGroup,
              GroupHeading: CollapsibleGroupHeading,
            }}
            collapsedGroups={collapsedGroups}
            setCollapsedGroups={setCollapsedGroups}
          /> */}

          {/* <Select
            isMulti
            closeMenuOnSelect={false}
            options={metricOptions}
            className="font13 search_competitors_select"
            hideSelectedOptions={false}
            components={{
              Option: CheckboxOption,
              Group: CollapsibleGroup,
              GroupHeading: CollapsibleGroupHeading,
            }}
            value={metricOptions
              .flatMap((group) => group.options)
              .filter((option) => metricDraft.includes(option.value))}
            onChange={(list) =>
              setMetricDraft([
                ...new Set((list || []).map((item) => item.value)),
              ])
            }
            collapsedGroups={collapsedGroups}
            setCollapsedGroups={setCollapsedGroups}
          /> */}

          <Select
            isMulti
            closeMenuOnSelect={false}
            options={sortedMetricOptions}
            className="font13 search_competitors_select"
            styles={APP_CONFIG?.CUSTOM_SELECT_STYLES}
            hideSelectedOptions={false}
            components={{
              Option: CheckboxOption,
              Group: CollapsibleGroup,
              GroupHeading: CollapsibleGroupHeading,
            }}
            value={sortedMetricOptions
              .flatMap((group) => group.options)
              .filter((option) => metricDraft.includes(option.value))}
            onChange={(list) =>
              setMetricDraft([
                ...new Set((list || []).map((item) => item.value)),
              ])
            }
            collapsedGroups={collapsedGroups}
            setCollapsedGroups={setCollapsedGroups}
          />

          <div className="mt-2 select_heatmap_apply_block">
            <button
              className="btn btn-sm select_heatmap_apply_btn"
              onClick={applyMetricFilter}
              disabled={metricDraft.length === 0 || updatingMetrics}
            >
              Apply
            </button>
            <button
              className="btn btn-light btn-sm ml-2 select_heatmap_cancel_btn"
              onClick={() => setShowMetricFilter(false)}
            >
              Cancel
            </button>
          </div>

          <small className="text-muted mt-2">
            Selected: {metricDraft.length}
          </small>
        </div>
      )}

      {updatingMetrics && (
        <div className="mt-2 text-primary font14">Updating filters...</div>
      )}

      {/* ── Heatmap company chips — always show ×, removes from heatmap filter only ── */}
      {heatmapCompanies.length > 0 && (
        <div className="mt-2 selected_companies_block">
          <label className="form-label fw-medium selected_companies_lable">
            Selected Companies
          </label>

          <div className="d-flex flex-wrap selected_companies_tags">
            {visibleHeatmapChips.map((item) => (
              <div
                key={item.value}
                className="badge badge-primary p-2 mr-2 mb-2 d-flex align-items-center selected_companies_badges"
              >
                {item.label}
                {/* × removes from heatmapCompanies only — master selectedCompetitors unchanged */}
                <span
                  className="ml-2"
                  style={{ cursor: "pointer", fontWeight: "bold" }}
                  onClick={() =>
                    setHeatmapCompanies((prev) =>
                      prev.filter((c) => c.value !== item.value)
                    )
                  }
                >
                  ×
                </span>
              </div>
            ))}
          </div>

          {/* Show more / Show less */}
          {heatmapCompanies.length > COMPANIES_SHOW_DEFAULT && (
            <button
              className="btn p-0 mt-1 show_more_finacial"
              onClick={() => setShowAllHeatmapCompanies((prev) => !prev)}
            >
              {showAllHeatmapCompanies
                ? "Show Less"
                : `Show More (${
                    heatmapCompanies.length - COMPANIES_SHOW_DEFAULT
                  } more)`}
            </button>
          )}
        </div>
      )}

      {/* ── Heatmap visual ── */}
      <div className="mt-2">
        {financialData.length > 0 && (
          <Heatmap
            competitors={heatmapCompanies}
            financials={financialData}
            required_metrics={selectedMetrics}
            METRICS_WITH_NAMES={APP_CONFIG?.METRICS_WITH_NAMES}
          />
        )}
      </div>
    </div>
  );
};

export default CompetitorHeatmapContainer;
